import { ApprovalRefundEnum } from 'api-hooks/common';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import {
  RefundModel,
  RefundMutationUpdateReleasePurchaseInput,
  ReleasePurchaseModel,
} from 'api-hooks/refund/refund';
import { NavigationRoutes } from 'common/constants';
import { RefundStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import HOCInput from 'components/common/hoc-input';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { FormState } from 'components/elements/form';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import TitleStatus from 'legacy-modules/common/title-status';
import useReleaseStatus from 'legacy-modules/refund/hooks/use-release-status';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { useRefundFormState } from '../../../hooks/use-refund-formstate';
import { ReleasePurchaseFormType } from '../form-type';
import ActivityPurchaseSection from '../refund-agent/activity';
import AgentDetailField from '../refund-agent/agent-detail-field';
import AgentPaymentField from '../refund-agent/agent-payment-field';
import CruisePurchaseSection from '../refund-agent/cruise';
import DocumentPurchaseSection from '../refund-agent/document';
import FlightPurchaseSection from '../refund-agent/flight';
import HotelPurchaseSection from '../refund-agent/hotel';
import InformationAgentField from '../refund-agent/information-agent-field';
import { FormDivider } from '../styles';

// import { FormType } from './form-type';

interface Props {
  refund?: RefundModel;
  approvalRefundType?: ApprovalRefundEnum;
  onSubmit: (
    input: RefundMutationUpdateReleasePurchaseInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ReleasePurchaseModel | undefined>;
}

export default function RefundAgentForm(props: Props) {
  const { refund, approvalRefundType } = props;
  const { t } = useTranslation();

  const releasePurchase = refund?.releasePurchase || undefined;
  const sale = refund?.releaseSale?.sale;
  const defaultValues = React.useMemo<ReleasePurchaseFormType>(() => {
    return {
      isHideAgent: !releasePurchase,
      releaseSale: {
        refId: sale?.refId,
        id: sale?.id,
      },
      releasePurchase: releasePurchase
        ? {
            agentId: releasePurchase.agent.id,
            amount: releasePurchase.amount,
            paymentableId: releasePurchase.paymentable.chequeGiro?.id
              ? releasePurchase.paymentable.chequeGiro?.id
              : releasePurchase.paymentable.id,
            paymentMethod: releasePurchase.paymentMethod.type,
            paymentMethodId: releasePurchase.paymentMethod.id,
            refId: releasePurchase.purchase.refId ?? '',
            id: {
              id: releasePurchase.purchase.id,
              label: `${releasePurchase.purchase.invoiceNumber} - ${releasePurchase.purchase.code} - ${releasePurchase.purchase.agent.name}`,
            },
            chequeNote: {
              availableAt: releasePurchase.paymentable.availableAt,
              bankAccountId: releasePurchase.paymentable.bankAccount?.id,
              chequeGiroId: releasePurchase.paymentable.chequeGiro?.id,
              expiredAt: releasePurchase.paymentable.expiredAt,
              issuedAt: releasePurchase.paymentable.issuedAt,
              referenceNumber: releasePurchase.paymentable.referenceNumber,
            },
            //Information Additional
            transactionAt: releasePurchase.purchase.transactionAt,
            total: releasePurchase.purchase.total,
            agent: releasePurchase.purchase.agent.name,
            paymentChannel: releasePurchase.purchase.paymentChannel?.name ?? '',
          }
        : {
            id: null,
            agentId: null,
            amount: 0,
            chequeNote: null,
            paymentableId: null,
            paymentMethodId: null,
            refId: null,

            //Information Additional
            agent: '',
            paymentChannel: '',
            total: 0,
            transactionAt: null,
          },
      transactionOrderType: releasePurchase?.purchase?.transactionType ?? '',
    };
  }, [sale?.id, sale?.refId, releasePurchase]);

  const methods = useForm({
    defaultValues,
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values: ReleasePurchaseFormType) => {
      try {
        await props.onSubmit(
          {
            ...values.releasePurchase,
            paymentableId:
              values.releasePurchase.paymentMethod === PaymentMethodEnum.Balance
                ? values.releasePurchase.agentId
                : values.releasePurchase.paymentableId,
          },
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const {
    editableAgent,
    editableCustomer,
    editableInformation,
    setEditableForm,
  } = useRefundFormState();

  const { releaseStatus } = useReleaseStatus(releasePurchase);

  const noEdit =
    !releaseStatus.isRequested ||
    approvalRefundType === ApprovalRefundEnum.Agent ||
    editableCustomer ||
    editableInformation;

  const onChangeEditable = React.useCallback(
    ({ editable }: { editable: boolean }) => {
      if (editable) {
        setEditableForm({
          agent: editable,
          customer: false,
          information: false,
        });
      } else {
        setEditableForm({
          agent: editable,
          customer: editableCustomer,
          information: editableInformation,
        });
      }
    },
    [editableCustomer, editableInformation, setEditableForm],
  );

  const { navigate } = useNavigation();
  const permission = useGetCrudPermission('release-purchases');
  const onNavigate = React.useCallback(() => {
    if (!permission.view) return;
    if (releasePurchase?.id) {
      navigate(NavigationRoutes.RefundReleaseAgentView, {
        id: releasePurchase?.id,
      });
    }
  }, [navigate, permission.view, releasePurchase?.id]);

  const customViewName =
    refund && [t('common:refund'), refund.number].join(' - ');

  const { requestApprovalButton } = useApprovalAction(
    'release-purchases',
    releasePurchase,
  );
  const { resolverButton } = useResolveApproval(
    'release-purchases',
    releasePurchase,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'release-purchases',
    releasePurchase,
  );

  const isActive = refund?.status === RefundStatusEnum.Processed;

  const onRenderRightComponent = React.useMemo(() => {
    if (!releasePurchase) return undefined;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {isActive && (
          <>
            {revertDraftButton}
            {requestApprovalButton}
            {resolverButton}
          </>
        )}
        <Button buttonType="secondary" onClick={onNavigate}>
          {t('refund:see_release')}
        </Button>
      </div>
    );
  }, [
    isActive,
    onNavigate,
    releasePurchase,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    t,
  ]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={editableAgent}
      customViewName={customViewName}
      noReset
    >
      <FormState>
        {({ editable }) => {
          onChangeEditable({ editable });
          return (
            <FormHeader
              data={refund}
              onDelete={() => {}}
              noDelete
              title={
                <TitleStatus
                  type="release"
                  title={t('refund:agent_refund')}
                  status={refund!.releasePurchase!.status}
                />
              }
              noEdit={noEdit}
              onRenderComponent={editable ? undefined : onRenderRightComponent}
            />
          );
        }}
      </FormState>
      <div>
        <FormState>
          {({ editable }) => (
            <InformationAgentField
              refund={refund}
              key={editable ? 'enabled' : 'disabled'}
            />
          )}
        </FormState>
        <HOCInput keys={['isHideAgent']}>
          {({ isHideAgent }) => (
            <>
              {!isHideAgent && (
                <>
                  <AgentPaymentField />
                  <AgentDetailField />
                </>
              )}
            </>
          )}
        </HOCInput>
        <FormState>
          {({ editable }) => (
            <React.Fragment key={editable ? 'enabled' : 'disabled'}>
              <FlightPurchaseSection refund={refund} />
              <HotelPurchaseSection refund={refund} />
              <CruisePurchaseSection refund={refund} />
              <ActivityPurchaseSection refund={refund} />
              <DocumentPurchaseSection refund={refund} />
            </React.Fragment>
          )}
        </FormState>
        <FormDivider />
      </div>
    </Form>
  );
}
